/**
 * Context provider for Transliteration Ranking Page state management
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { NameEntry, Candidate } from './types'
import { parseFile, validateFileSize, validateFileType } from './utils/fileParser'
import { useLocalStorage } from './hooks/useLocalStorage'

interface TransliterationEvalContextValue {
  // Data state
  uploadedData: NameEntry[] | null
  currentIndex: number
  evaluations: Record<string, Candidate[]>

  // Computed values
  totalNames: number
  evaluatedCount: number
  currentName: NameEntry | null
  hasEvaluated: (nameId: string) => boolean

  // UI state
  isUploading: boolean
  uploadError: string | null

  // Actions
  uploadFile: (file: File) => Promise<void>
  saveEvaluation: (nameId: string, reorderedCandidates: Candidate[], skipStorageSave?: boolean) => void
  moveToNext: (skipStorageSave?: boolean) => void
  moveToPrevious: (skipStorageSave?: boolean) => void
  jumpToIndex: (index: number) => void
  resetEvaluation: () => void
  loadSavedState: () => Promise<boolean>
}

const TransliterationEvalContext = createContext<TransliterationEvalContextValue | undefined>(undefined)

export const useTransliterationEval = (): TransliterationEvalContextValue => {
  const context = useContext(TransliterationEvalContext)
  if (!context) {
    throw new Error('useTransliterationEval must be used within TransliterationEvalProvider')
  }
  return context
}

interface TransliterationEvalProviderProps {
  children: React.ReactNode
}

export const TransliterationEvalProvider: React.FC<TransliterationEvalProviderProps> = ({ children }) => {
  const [uploadedData, setUploadedData] = useState<NameEntry[] | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [evaluations, setEvaluations] = useState<Record<string, Candidate[]>>({})
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const { saveState, loadState, clearState } = useLocalStorage()

  // Computed values
  const totalNames = uploadedData?.length || 0
  const evaluatedCount = Object.keys(evaluations).length

  // Get current name with evaluated candidates merged in
  const currentName = useMemo(() => {
    if (!uploadedData || currentIndex >= uploadedData.length) return null

    const name = uploadedData[currentIndex]
    const evaluatedCandidates = evaluations[name.id]

    if (evaluatedCandidates) {
      return {
        ...name,
        candidates: evaluatedCandidates,
        evaluated: true,
      }
    }

    return name
  }, [uploadedData, currentIndex, evaluations])

  const hasEvaluated = useCallback(
    (nameId: string) => {
      return nameId in evaluations
    },
    [evaluations]
  )

  // Upload file
  const uploadFile = useCallback(
    async (file: File) => {
      setIsUploading(true)
      setUploadError(null)

      try {
        // Validate file
        validateFileType(file)
        validateFileSize(file)

        // Parse file
        const parsedNames = await parseFile(file)

        // Set state
        setUploadedData(parsedNames)
        setCurrentIndex(0)
        setEvaluations({})
        await clearState()

        setIsUploading(false)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
        setUploadError(errorMessage)
        setIsUploading(false)
        throw err
      }
    },
    [clearState]
  )

  // Save evaluation for current name (optionally skip storage save)
  const saveEvaluation = useCallback(
    (nameId: string, reorderedCandidates: Candidate[], skipStorageSave = false) => {
      // Update current ranks based on array position
      const candidatesWithUpdatedRanks = reorderedCandidates.map((candidate, index) => ({
        ...candidate,
        currentRank: index + 1,
      }))

      setEvaluations((prev) => {
        const updatedEvaluations = {
          ...prev,
          [nameId]: candidatesWithUpdatedRanks,
        }

        // Save to session storage with the updated evaluations (unless skipped)
        if (!skipStorageSave && uploadedData) {
          // Fire and forget - don't await to keep synchronous behavior
          saveState(uploadedData, currentIndex, updatedEvaluations).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('Failed to persist evaluation state:', err)
          })
        }

        return updatedEvaluations
      })
    },
    [uploadedData, currentIndex, saveState]
  )

  // Navigation
  const moveToNext = useCallback(
    (skipStorageSave = false) => {
      if (currentIndex < totalNames - 1) {
        const nextIndex = currentIndex + 1
        setCurrentIndex(nextIndex)

        // Save to session storage - use current evaluations from state (unless skipped)
        if (!skipStorageSave) {
          setEvaluations((currentEvals) => {
            if (uploadedData) {
              // Fire and forget - don't await to keep synchronous behavior
              saveState(uploadedData, nextIndex, currentEvals).catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('Failed to persist navigation state:', err)
              })
            }
            return currentEvals
          })
        }
      }
    },
    [currentIndex, totalNames, uploadedData, saveState]
  )

  const moveToPrevious = useCallback(
    (skipStorageSave = false) => {
      if (currentIndex > 0) {
        const prevIndex = currentIndex - 1
        setCurrentIndex(prevIndex)

        // Save to session storage - keep evaluations intact (unless skipped)
        if (!skipStorageSave) {
          setEvaluations((currentEvals) => {
            if (uploadedData) {
              // Fire and forget - don't await to keep synchronous behavior
              saveState(uploadedData, prevIndex, currentEvals).catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('Failed to persist navigation state:', err)
              })
            }
            return currentEvals
          })
        }
      }
    },
    [currentIndex, uploadedData, saveState]
  )

  const jumpToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalNames) {
        setCurrentIndex(index)

        // Save to session storage - use current evaluations from state
        setEvaluations((currentEvals) => {
          if (uploadedData) {
            // Fire and forget - don't await to keep synchronous behavior
            saveState(uploadedData, index, currentEvals).catch((err) => {
              // eslint-disable-next-line no-console
              console.warn('Failed to persist navigation state:', err)
            })
          }
          return currentEvals
        })
      }
    },
    [totalNames, uploadedData, saveState]
  )

  // Reset everything
  const resetEvaluation = useCallback(() => {
    setUploadedData(null)
    setCurrentIndex(0)
    setEvaluations({})
    setUploadError(null)
    // Fire and forget - don't await to keep synchronous behavior
    clearState().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('Failed to clear session storage:', err)
    })
  }, [clearState])

  // Load saved state from session storage
  const loadSavedState = useCallback(async () => {
    const saved = await loadState()
    if (saved) {
      setUploadedData(saved.uploadedData)
      setCurrentIndex(saved.currentIndex)
      setEvaluations(saved.evaluations)
      return true
    }
    return false
  }, [loadState])

  const value = useMemo(
    () => ({
      uploadedData,
      currentIndex,
      evaluations,
      totalNames,
      evaluatedCount,
      currentName,
      hasEvaluated,
      isUploading,
      uploadError,
      uploadFile,
      saveEvaluation,
      moveToNext,
      moveToPrevious,
      jumpToIndex,
      resetEvaluation,
      loadSavedState,
    }),
    [
      uploadedData,
      currentIndex,
      evaluations,
      totalNames,
      evaluatedCount,
      currentName,
      hasEvaluated,
      isUploading,
      uploadError,
      uploadFile,
      saveEvaluation,
      moveToNext,
      moveToPrevious,
      jumpToIndex,
      resetEvaluation,
      loadSavedState,
    ]
  )

  return <TransliterationEvalContext.Provider value={value}>{children}</TransliterationEvalContext.Provider>
}
